/**
 * @file resolve.ts
 * @description The one place nav gate logic lives. Given caller-gathered
 * {@link NavSignals}, it answers what the sidebar sections, the top nav and the
 * command palette list should show. Destinations carry their own gates in
 * `@/config/navigation` (`surface`, `navRequires`, `requiresArbiterConfigured`,
 * `requiresCallingOn`, `flag`); a surface's requirement comes from the same surface map the layout
 * guards read (`@/lib/access/surface-map`), checked against the viewer's access.
 *
 * Pure: no React, PostHog or Clerk. Nav visibility is presentation, not
 * authorization — route guards live in the layouts and the backend.
 */

import { navigationConfig, navigationSurfaces, topNavConfig, type NavItem, type NavSection } from '@/config/navigation'
import { SCHOOL_SECTION_KEYS } from '@/config/navigation-key'
import { SURFACES, type SurfaceKey } from '@/lib/access/surface-map'
import { holds, type ViewerAccess } from '@/lib/access/viewer-access'
import type { NavFlagKey, NavSignals, TopNavItem, VisibleDestination } from './types'

const SCHOOL_AREA_KEYS: readonly string[] = SCHOOL_SECTION_KEYS

/** The palette's emission order. */
const DESTINATION_KEY_ORDER = ['manage', 'comms', 'events', 'support', 'admin', 'settings'] as const

/** Whether the surface would admit the viewer; no surface means ungated. */
function opensSurface(viewer: ViewerAccess, surface: SurfaceKey | null | undefined): boolean {
  return surface ? holds(viewer, SURFACES[surface].requires) : true
}

function passesFlag(flag: NavFlagKey | undefined, s: NavSignals): boolean {
  // Collapse anything but a literal `true` to hidden. Never `!== false`.
  return flag ? s.isFlagEnabled(flag) === true : true
}

function isItemVisible(item: NavItem, navKey: string, s: NavSignals): boolean {
  if (!opensSurface(s.viewer, navigationSurfaces[navKey]) || !opensSurface(s.viewer, item.surface)) return false
  if (item.requiresArbiterConfigured && !s.isArbiterConfigured) return false
  if (item.requiresCallingOn && !s.isCallingOn) return false
  return passesFlag(item.flag, s)
}

/**
 * The sidebar sections for `navKey`, with gated items removed and emptied
 * sections dropped. Disabled and `#` items stay: the sidebar greys them.
 */
export function visibleSections(navKey: string | null, signals: NavSignals): NavSection[] {
  if (navKey === null || !(navKey in navigationSurfaces)) return []
  const sections = navigationConfig[navKey]
  if (!sections) return []

  return sections
    .map(section => ({ ...section, items: section.items.filter(item => isItemVisible(item, navKey, signals)) }))
    .filter(section => section.items.length > 0)
}

/** The top-nav links the viewer sees. Empty until school setup is complete. */
export function visibleTopNav(signals: NavSignals): TopNavItem[] {
  if (!signals.isSchoolSetupComplete) return []
  return topNavConfig.filter(
    item =>
      opensSurface(signals.viewer, item.surface) &&
      (!item.navRequires || holds(signals.viewer, item.navRequires)) &&
      passesFlag(item.flag, signals)
  )
}

/** A real, navigable destination: not disabled and not a `#` placeholder. */
function isRealDestination(item: NavItem): boolean {
  return item.disabled !== true && item.url !== '#'
}

function destinationsForKey(navKey: string, signals: NavSignals): VisibleDestination[] {
  return (navigationConfig[navKey] ?? []).flatMap(section =>
    section.items
      .filter(item => isRealDestination(item) && isItemVisible(item, navKey, signals))
      .map(item => ({ navKey, section, item }))
  )
}

/**
 * Every navigable destination the viewer may jump to, in palette order. Skips
 * the school areas without an active school, and drops disabled and `#` items.
 */
export function visibleDestinations(signals: NavSignals): VisibleDestination[] {
  const keys = DESTINATION_KEY_ORDER.filter(navKey => signals.hasActiveSchool || !SCHOOL_AREA_KEYS.includes(navKey))
  return keys.flatMap(navKey => destinationsForKey(navKey, signals))
}
